import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { EmptyState, IconButton, ScorableTab, TabList, Tabs } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";
import { colorsPalettes } from "@similarweb/styles";

import { showErrorToast } from "app/actions/toast_actions";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import ListPageHeader from "app/pages/sales-intelligence/common-components/header/ListPageHeader/ListPageHeader";
import { QueryStates } from "app/pages/sales-intelligence/common-components/QueryStates/QueryStates";
import { AssignedUsersTable } from "app/pages/sales-intelligence/modules/teams/components/AssignedUsersTable/AssignedUsersTable";
import { DeleteTeamModal } from "app/pages/sales-intelligence/modules/teams/components/DeleteTeamModal/DeleteTeamModal";
import { TeamModalContainer } from "app/pages/sales-intelligence/modules/teams/components/TeamModal/TeamModalContainer";
import type { TeamsColumnHandlers } from "app/pages/sales-intelligence/modules/teams/components/TeamsTable/columns";
import { TeamsTable } from "app/pages/sales-intelligence/modules/teams/components/TeamsTable/TeamsTable";
import { UnassignedUsersTable } from "app/pages/sales-intelligence/modules/teams/components/UnassignedUsersTable/UnassignedUsersTable";
import { useTeamsTrackingService } from "app/pages/sales-intelligence/modules/teams/hooks/useTeamsTrackingService";
import { useDeleteTeamMutation } from "app/pages/sales-intelligence/modules/teams/queries/useDeleteTeamMutation";
import { useTeamAccountUsersQuery } from "app/pages/sales-intelligence/modules/teams/queries/useTeamAccountUsersQuery";
import { useTeamsListQuery } from "app/pages/sales-intelligence/modules/teams/queries/useTeamsListQuery";
import type { TeamListItem } from "app/pages/sales-intelligence/modules/teams/types/teams.types";
import { filterTeamsBySearch } from "app/pages/sales-intelligence/modules/teams/utils/filterTeamsBySearch";
import { partitionAccountUsers } from "app/pages/sales-intelligence/modules/teams/utils/partitionAccountUsers";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import {
    TEAMS_MANAGEMENT_TAB,
    TEAMS_MANAGEMENT_TAB_AUTOMATION_MAP,
    TEAMS_MANAGEMENT_TAB_LABEL_MAP,
    TEAMS_MANAGEMENT_TAB_OPTIONS,
    TEAMS_MANAGEMENT_TAB_TRACKING_MAP,
    type TeamsManagementTab,
} from "./constants";
import { StyledTabValueLoader, StyledTabsWrapper, StyledTeamsManagementContainer } from "./styles";
import { TeamFilterTitlesProvider } from "./TeamFilterTitlesProvider";

const TAB_VALUE_PLACEHOLDER = "—";

export const TeamsManagementContainer = () => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [teamsSearchTerm, setTeamsSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState<TeamsManagementTab>(TEAMS_MANAGEMENT_TAB.teams);
    const [teamToDelete, setTeamToDelete] = useState<TeamListItem | undefined>(undefined);

    const {
        data: teams,
        isLoading: isTeamsLoading,
        isError: isTeamsError,
        refetch: refetchTeams,
    } = useTeamsListQuery();
    const {
        data: accountUsers = [],
        isLoading: isAccountUsersLoading,
        isError: isAccountUsersError,
        refetch: refetchAccountUsers,
    } = useTeamAccountUsersQuery();

    const { mutateAsync: deleteTeam, isPending: isDeleting } = useDeleteTeamMutation();
    const tracking = useTeamsTrackingService();

    const teamRows = useMemo(() => filterTeamsBySearch(teams ?? [], teamsSearchTerm), [teams, teamsSearchTerm]);
    const partition = useMemo(() => partitionAccountUsers(teams ?? [], accountUsers), [teams, accountUsers]);

    const selectedTabIndex = TEAMS_MANAGEMENT_TAB_OPTIONS.indexOf(activeTab);

    const handleTabChange = useCallback(
        (index: number) => {
            const nextTab = TEAMS_MANAGEMENT_TAB_OPTIONS[index];
            if (!nextTab || nextTab === activeTab) {
                return;
            }
            tracking.trackTabSwitch(TEAMS_MANAGEMENT_TAB_TRACKING_MAP[nextTab]);
            setActiveTab(nextTab);
        },
        [tracking, activeTab],
    );

    const getTabValue = useCallback(
        (tab: TeamsManagementTab): ReactNode => {
            if (tab === TEAMS_MANAGEMENT_TAB.teams) {
                return String(teams?.length ?? 0);
            }

            if (isAccountUsersLoading) {
                return <StyledTabValueLoader id={`teams-management-tab-value-loader-${tab}`} />;
            }

            if (isAccountUsersError) {
                return TAB_VALUE_PLACEHOLDER;
            }

            if (tab === TEAMS_MANAGEMENT_TAB.assignedUsers) {
                return `${partition.assignedCount}/${partition.totalCount}`;
            }

            return String(partition.unassignedCount);
        },
        [teams, isAccountUsersLoading, isAccountUsersError, partition],
    );

    const handlers = useMemo<TeamsColumnHandlers>(
        () => ({
            onEdit: (team) => {
                tracking.trackOpenTeamDetail(team.name ?? "", "edit");
                void siNavigator.goToTeamDetailPage({ id: team.teamId });
            },
            onDelete: (team) => {
                setTeamToDelete(team);
            },
        }),
        [tracking],
    );

    const handleOpenCreateModal = useCallback(
        (source: "header" | "empty_state") => {
            tracking.trackOpenCreateTeamModal(source);
            setIsCreateModalOpen(true);
        },
        [tracking],
    );

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const handleCancelDelete = () => {
        if (isDeleting) {
            return;
        }
        setTeamToDelete(undefined);
    };

    const handleConfirmDelete = async () => {
        if (!teamToDelete) {
            return;
        }
        try {
            await deleteTeam(teamToDelete.teamId);
            tracking.trackTeamDeleted(teamToDelete.name ?? "");
            setTeamToDelete(undefined);
        } catch {
            dispatch(
                showErrorToast(
                    renderErrorToast({
                        content: translate("si.common.toast.error"),
                        minWidth: "auto",
                    }),
                ),
            );
            // Keep modal open so the user can retry.
        }
    };

    const renderEmptyStateCta = useCallback(
        () => (
            <IconButton
                dataAutomation="teams-management-empty-state-add-button"
                iconSize="xs"
                iconName="add"
                onClick={() => handleOpenCreateModal("empty_state")}
            >
                {translate("si.management.teams.button.add")}
            </IconButton>
        ),
        [translate, handleOpenCreateModal],
    );

    const renderActiveTabContent = () => {
        if (activeTab === TEAMS_MANAGEMENT_TAB.teams) {
            if ((teams?.length ?? 0) === 0) {
                return (
                    <Flex
                        flex="1"
                        justifyContent="center"
                        width="100%"
                        data-automation="teams-management-empty-state"
                        margin="0 auto"
                        padding="30px 0"
                        background={colorsPalettes.carbon["0"]}
                    >
                        <EmptyState
                            title={translate("si.management.teams.empty.title")}
                            subtitle={translate("si.management.teams.empty.subtitle")}
                            CtaButtons={renderEmptyStateCta}
                        />
                    </Flex>
                );
            }

            return (
                <TeamFilterTitlesProvider>
                    <TeamsTable
                        rows={teamRows}
                        handlers={handlers}
                        searchTerm={teamsSearchTerm}
                        onSearchChange={setTeamsSearchTerm}
                    />
                </TeamFilterTitlesProvider>
            );
        }

        if (isAccountUsersLoading || isAccountUsersError) {
            return (
                <QueryStates
                    isLoading={isAccountUsersLoading}
                    isError={isAccountUsersError}
                    onRetry={() => void refetchAccountUsers()}
                    errorDataAutomation="teams-management-account-users-error"
                    errorRetryDataAutomation="teams-management-account-users-error-retry"
                />
            );
        }

        if (activeTab === TEAMS_MANAGEMENT_TAB.assignedUsers) {
            return (
                <AssignedUsersTable
                    rows={partition.assigned}
                    teams={teams ?? []}
                />
            );
        }

        return (
            <UnassignedUsersTable
                rows={partition.unassigned}
                teams={teams ?? []}
            />
        );
    };

    return (
        <StyledTeamsManagementContainer
            data-automation="teams-management-page"
            gap="30px"
            margin="0 auto"
            width="100%"
            flex="1"
            minHeight="0"
        >
            <ListPageHeader
                title={{ key: "si.management.teams.header" }}
                description={{ key: "si.management.teams.header.line_1" }}
                titleDataAutomation="teams-management-header"
                descriptionDataAutomation="teams-management-header-line-1"
            >
                <Flex
                    position="absolute"
                    top={0}
                    right={0}
                    alignItems="center"
                    gap="8px"
                >
                    <IconButton
                        dataAutomation="add-team-button"
                        iconSize="xs"
                        iconName="add"
                        onClick={() => handleOpenCreateModal("header")}
                    >
                        {translate("si.management.teams.button.add")}
                    </IconButton>
                </Flex>
            </ListPageHeader>

            <QueryStates
                isLoading={isTeamsLoading}
                isError={isTeamsError}
                onRetry={() => void refetchTeams()}
                errorDataAutomation="teams-management-list-error"
                errorRetryDataAutomation="teams-management-list-error-retry"
            />

            {!isTeamsLoading && !isTeamsError && teams ? (
                <Stack
                    flex="1"
                    minWidth={0}
                    width="100%"
                >
                    <StyledTabsWrapper data-automation="teams-management-tabs">
                        <Tabs
                            selectedIndex={selectedTabIndex}
                            onSelect={handleTabChange}
                        >
                            <TabList>
                                {TEAMS_MANAGEMENT_TAB_OPTIONS.map((tab) => (
                                    <ScorableTab
                                        key={tab}
                                        metric={translate(TEAMS_MANAGEMENT_TAB_LABEL_MAP[tab])}
                                        value={getTabValue(tab) as string}
                                        className={TEAMS_MANAGEMENT_TAB_AUTOMATION_MAP[tab]}
                                    />
                                ))}
                            </TabList>
                        </Tabs>
                    </StyledTabsWrapper>

                    <Stack
                        flex="1"
                        minHeight="0"
                        margin="0 0 40px"
                        data-automation="teams-management-tab-content"
                    >
                        {renderActiveTabContent()}
                    </Stack>
                </Stack>
            ) : null}

            <TeamModalContainer
                isOpen={isCreateModalOpen}
                onClose={handleCloseCreateModal}
            />

            <DeleteTeamModal
                isOpen={teamToDelete !== undefined}
                teamName={teamToDelete?.name ?? ""}
                isDeleting={isDeleting}
                onCancel={handleCancelDelete}
                onConfirm={() => void handleConfirmDelete()}
            />
        </StyledTeamsManagementContainer>
    );
};
